import React, { useState, useEffect } from "react";
import "./LoginPage.css";
import logo from "../assets/logo.png";
import coin from "../assets/Cam3.png";
import loginSupport from "../assets/login-support.png";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FaEye, FaEyeSlash } from "react-icons/fa";

/* ================= WHATSAPP FLOAT COMPONENT ================= */
const WhatsAppFloat = ({
  phoneNumber = "15485825756",
  message = "Hello! I need assistance with logging into my SafepalPay account.",
  position = "right",
  bottom = "30px",
  right = "30px",
  left = "auto",
  size = "54px",
  iconSize = "28px",
  pulseEffect = true,
  className = "",
  style = {}
}) => {
  const formattedNumber = phoneNumber.replace(/[^\d]/g, "");
  const whatsappUrl = `https://wa.me/${formattedNumber}?text=${encodeURIComponent(message)}`;

  const positionStyles = position === "left"
    ? { left: left || "20px", right: "auto" }
    : { right: right || "20px", left: "auto" };

  const combinedStyles = {
    position: "fixed",
    bottom: bottom,
    width: size,
    height: size,
    borderRadius: "50%",
    backgroundColor: "#25d366",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
    zIndex: 10000,
    cursor: "pointer",
    transition: "all 0.3s ease",
    textDecoration: "none",
    ...positionStyles,
    ...style
  };

  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className={`whatsapp-float-unique ${pulseEffect ? "pulse-unique" : ""} ${className}`}
      style={combinedStyles}
      aria-label="Chat on WhatsApp"
      title="Chat on WhatsApp for login support"
    >
      <svg
        width={iconSize}
        height={iconSize}
        viewBox="0 0 32 32"
        fill="white"
      >
        <path d="M16.001 3C9.373 3 4 8.373 4 15.001c0 2.645.867 5.084 2.329 7.058L5 29l7.142-1.875A11.96 11.96 0 0016 27c6.627 0 12-5.373 12-11.999C28 8.373 22.627 3 16.001 3zm0 21.999c-1.924 0-3.715-.57-5.219-1.548l-.372-.23-4.242 1.114 1.133-4.135-.242-.384A9.957 9.957 0 016 15.001C6 9.486 10.486 5 16.001 5 21.515 5 26 9.486 26 15.001c0 5.514-4.485 9.998-9.999 9.998zm5.548-7.557c-.303-.151-1.793-.886-2.071-.987-.279-.101-.483-.151-.687.151-.202.302-.787.987-.965 1.19-.177.202-.354.227-.656.075-.303-.151-1.279-.471-2.435-1.5-.9-.802-1.508-1.792-1.686-2.094-.177-.303-.019-.467.133-.619.136-.135.303-.354.455-.531.152-.177.202-.303.303-.505.101-.202.051-.379-.025-.531-.076-.151-.687-1.656-.941-2.269-.247-.592-.498-.512-.687-.521-.177-.008-.379-.01-.582-.01-.202 0-.531.075-.808.379-.278.303-1.06 1.035-1.06 2.524 0 1.488 1.085 2.926 1.237 3.128.151.202 2.14 3.27 5.183 4.588.724.312 1.288.498 1.728.637.726.231 1.387.198 1.911.12.583-.087 1.793-.733 2.047-1.44.253-.707.253-1.313.177-1.44-.076-.126-.278-.198-.581-.354z" />
      </svg>
    </a>
  );
};

const LoginPage = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [popup, setPopup] = useState({
    show: false,
    message: "",
    success: false,
  });

  // Crisp Chat Integration
  useEffect(() => {
    window.$crisp = window.$crisp || [];
    window.CRISP_WEBSITE_ID = "b5635951-f13a-4b92-95d7-c1e3666f3abf";

    window.$crisp.push(["do", "chat:show"]);

    if (!document.querySelector('script[src="https://client.crisp.chat/l.js"]')) {
      const script = document.createElement("script");
      script.src = "https://client.crisp.chat/l.js";
      script.async = true;
      document.head.appendChild(script);
    }

    return () => {
      if (window.$crisp) {
        window.$crisp.push(["do", "chat:hide"]);
      }
    };
  }, []);

  // Pass user email to Crisp if user is already logged in
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser && window.$crisp) {
      try {
        const userData = JSON.parse(storedUser);
        if (userData.email) {
          window.$crisp.push(["set", "user:email", userData.email]);
          if (userData.name) {
            window.$crisp.push(["set", "user:nickname", userData.name]);
          }
        }
      } catch (e) {
        console.error("Error parsing user data", e);
      }
    }
  }, []);

  const handleLogin = async () => {
    if (!email || !password) {
      setPopup({
        show: true,
        message: "Please enter email and password",
        success: false,
      });
      return;
    }

    setIsLoading(true);

    try {
      const res = await axios.post(
        "https://backend-safepal.onrender.com/api/auth/login",
        { email, password }
      );

      if (res.data.token) {
        const user = res.data.data;

        localStorage.setItem("token", res.data.token);
        localStorage.setItem("user", JSON.stringify(res.data.data));
        localStorage.setItem("userEmail", user.email);
        localStorage.setItem("userName", user.name);
        localStorage.setItem("userId", user.id);
        localStorage.setItem("userReferralCode", user.referralCode);

        if (window.$crisp) {
          window.$crisp.push(["set", "user:email", user.email]);
          window.$crisp.push(["set", "user:nickname", user.name]);
        }
      }

      setPopup({
        show: true,
        message: "Login successful!",
        success: true,
      });

      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (error) {
      setPopup({
        show: true,
        message: error.response?.data?.error || "Invalid email or password",
        success: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="lp-container-unique">
        <div className="lp-card-unique">
          <span className="getstarted-back-unique" onClick={() => navigate(-1)}>←</span>

          <div className="lp-top-logo-unique">
            <img src={logo} alt="SafepalPay" />
          </div>

          <div className="lp-coin-wrapper-unique">
            <img src={coin} alt="Security Coin" />
          </div>

          <h2 className="lp-title-unique">Login</h2>

          {/* Login Support Image Added */}
          <div className="lp-login-image-unique">
            <img src={loginSupport} alt="Login Support Illustration" />
          </div>

          <div className="lp-form-box-unique">
            <div className="lp-form-group-unique">
              <label className="lp-label-unique">Email</label>
              <input
                type="email"
                className="lp-input-unique"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="lp-form-group-unique">
              <label className="lp-label-unique">Password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  className="lp-input-unique"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                />
                <span
                  onClick={() => !isLoading && setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: "18px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: isLoading ? "not-allowed" : "pointer",
                    opacity: isLoading ? 0.5 : 1,
                  }}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
            </div>

            <p className="lp-forgot-password-unique">
              <button
                className="lp-link-btn-forgot-unique"
                onClick={() => navigate("/forgotpassword")}
                disabled={isLoading}
              >
                Forgot Password?
              </button>
            </p>

            <button 
              className="lp-login-btn-unique" 
              onClick={handleLogin}
              disabled={isLoading}
            >
              {isLoading ? "Logging in..." : "Login"}
            </button>
          </div>

          <p className="lp-login-link-unique">
            Don't have an account?{" "}
            <button
              className="lp-link-btn-unique"
              onClick={() => navigate("/getstarted")}
              disabled={isLoading}
            >
              Sign up here
            </button>
          </p>
        </div>

        {/* ANIMATED POPUP */}
        {popup.show && (
          <div className="lp-popup-overlay-unique">
            <div className="lp-popup-card-unique">
              <div className={`lp-icon-box-unique ${popup.success ? "success-unique" : "error-unique"}`}>
                <svg viewBox="0 0 100 100" className="lp-icon-unique">
                  <circle cx="50" cy="50" r="45" className="lp-circle-unique" />
                  <path
                    className="lp-path-unique"
                    d={
                      popup.success
                        ? "M30 52 L45 65 L70 38"
                        : "M35 35 L65 65 M65 35 L35 65"
                    }
                  />
                </svg>
              </div>

              <p className="lp-popup-text-unique">{popup.message}</p>

              <button
                className="lp-ok-btn-unique"
                onClick={() => setPopup({ ...popup, show: false })}
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>

      {/* WhatsApp Float Button - For Login Assistance */}
      <WhatsAppFloat
        phoneNumber="15485825756"
        message="Hello! I need assistance with logging into my SafepalPay account."
        bottom="30px"
        right="30px"
        pulseEffect={true}
      />
    </>
  );
};

export default LoginPage;